'use client';

import { useHeaderScroll } from '@/components/NewHeader/useHeaderScroll';
import { useTranslation } from '@/contexts/dictionaryContext';
import Image from 'next/image';
import { useSyncExternalStore } from 'react';
import headerMobile from '../../../public/header-mobile2.jpg';

// Same breakpoint as the Tailwind `desktop` screen
const DESKTOP_QUERY = '(min-width: 1200px)';

function subscribeToDesktop(onChange: () => void) {
  const mediaQuery = window.matchMedia(DESKTOP_QUERY);
  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
}

// Hidden with CSS, the video was still downloaded on mobile (~5 MB). Render it only on
// desktop; the server snapshot is false, so SSR and hydration render the image alone.
function HeroVideo() {
  const isDesktop = useSyncExternalStore(
    subscribeToDesktop,
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => false,
  );

  if (!isDesktop) return null;

  return (
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 brightness-[.6] object-cover object-center w-full h-full desktop:aspect-video hidden desktop:block">
      <source
        src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/uploads/video/Villatravellers_short_1280.webm"
        type="video/webm"
      />
      <source
        src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/uploads/video/Villatravellers_short_1280.mp4"
        type="video/mp4"
      />
    </video>
  );
}

export function Hero() {
  const { isHome } = useHeaderScroll();
  const { home } = useTranslation();

  if (!isHome) return null;

  return (
    <section className="w-full aspect-[9/16] lg:aspect-video max-h-[90vh] relative flex justify-center items-center">
      <Image
        src={headerMobile}
        alt="header-mobile"
        fill
        loading="eager"
        fetchPriority="high"
        className="absolute w-full object-cover object-center brightness-[.6]"
      />

      <HeroVideo />

      <div className="px-2 desktop:px-28 pb-8 tablet:pb-0 w-full z-[1]">
        <h1 className="font-title text-3xl desktop:text-[40px] text-center pb-4 desktop:pb-10 leading-[1.25] font-bold text-white/90">
          {home.bannerTitle}
        </h1>

        <div className="flex flex-col desktop:gap-1">
          {home.bannerSubtitle.map((line, index) => (
            <p
              key={index}
              className="font-averta-light text-base desktop:text-xl text-center text-white/90">
              {line}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}
